import { Inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { CentralInstance } from '@portal-core/auth/models/central-instance.model';
import { WINDOW } from '@portal-core/util/window.provider';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly ArchivePath: string = 'archive';
  private readonly AssetPath: string = 'asset';
  private readonly ContentPath: string = 'content';
  private readonly PublishPath: string = 'publish';

  /**
   * The base URI for the global Central API.
   */
  get centralGlobalApiBaseUri(): string {
    return this._centralGlobalApiBaseUri;
  }
  private _centralGlobalApiBaseUri: string = environment.centralApiBaseUri;

  /**
   * The base URI for the Central API.
   * The URI contains the license subdomain if the portal was accessed with a subdomain, eg `example.api.madcapcentral.com`.
   * The URI also contains the Central instance if `setInstanceUrl` has been called, eg `example.api.eugwc.madcapcentral.com`.
   */
  get centralApiBaseUri(): string {
    return this._centralApiBaseUri;
  }
  private _centralApiBaseUri: string = environment.centralApiBaseUri;

  /**
   * The base URI for the Collab API.
   * The URI contains the license subdomain if the portal was accessed with a subdomain, eg `example.collab-api.madcapcentral.com`.
   * The URI also contains the Central instance if `setInstanceUrl` has been called, eg `example.collab-api.eugwc.madcapcentral.com`.
   */
  get collabApiUrl(): string {
    return this._collabApiUrl;
  }
  private _collabApiUrl: string = environment.collabApiUrl;

  /**
   * The base URI for the Collab Authority Service.
   * The URI contains the license subdomain if the portal was accessed with a subdomain, eg `example.collab.madcapcentral.com`.
   * The URI also contains the Central instance if `setInstanceUrl` has been called, eg `example.collab.eugwc.madcapcentral.com`.
   */
  get collabAuthorityUrl(): string {
    return this._collabAuthorityUrl;
  }
  private _collabAuthorityUrl: string = environment.collabAuthorityUrl;

  /**
   * The base URI for the Collab Workspace Service.
   * The URI contains the license subdomain if the portal was accessed with a subdomain, eg `example.collab.madcapcentral.com`.
   * The URI also contains the Central instance if `setInstanceUrl` has been called, eg `example.collab.eugwc.madcapcentral.com`.
   */
  get collabWorkspaceUrl(): string {
    return this._collabWorkspaceUrl;
  }
  private _collabWorkspaceUrl: string = environment.collabWorkspaceUrl;

  /**
 * The base URI for the Central instance's archives.
 * The base URI contains the Central instance code if `setInstanceCode` has been called, eg `example.api.eugwc.madcapcentral.com/archive`.
 */
  get centralArchiveBaseUri(): string {
    return this._centralArchiveBaseUri;
  }
  private _centralArchiveBaseUri: string = this.ArchivePath;

  /**
   * The base URI for the Central instance's assets.
   * The base URI contains the Central instance code if `setInstanceCode` has been called, eg `example.api.eugwc.madcapcentral.com/asset`.
   */
  get centralAssetsBaseUri(): string {
    return this._centralAssetsBaseUri;
  }
  private _centralAssetsBaseUri: string = this.AssetPath;

  /**
   * The base URI for the Central instance's content.
   * The base URI contains the Central instance code if `setInstanceCode` has been called, eg `example.api.eugwc.madcapcentral.com/content`.
   */
  get centralContentBaseUri(): string {
    return this._centralContentBaseUri;
  }
  private _centralContentBaseUri: string = this.ContentPath;

  /**
   * The base URI for the Central instance's published sites.
   * The base URI contains the Central instance code if `setInstanceCode` has been called, eg `example.api.eugwc.madcapcentral.com/publish`.
   */
  get centralPublishBaseUri(): string {
    return this._centralPublishBaseUri;
  }
  private _centralPublishBaseUri: string = this.PublishPath;

  /**
   * The base URI for the Status Page API.
   */
  get statusPageApiBaseUri(): string {
    return this._statusPageApiBaseUri;
  }
  private _statusPageApiBaseUri: string = environment.statusPageApiBaseUri;

  /**
   * The URI of the public Status Page UI.
   */
  get statusPageUri(): string {
    return this._statusPageUri;
  }
  private _statusPageUri: string = environment.statusPageUri;

  /**
   * The Central instance that is currently being used for API, collab, and blob requests.
   */
  get centralInstance(): CentralInstance {
    return this._centralInstance;
  }
  private _centralInstance: CentralInstance;

  public centralInstance$: BehaviorSubject<CentralInstance> = new BehaviorSubject<CentralInstance>(undefined);

  /**
   * The Central instance code that is currently being used for API, collab, and blob requests.
   */
  get centralInstanceInstanceCode(): string {
    return this._centralInstance?.InstanceCode;
  }

  constructor(@Inject(WINDOW) private window: Window) {
    if (environment.subdomainPortal) {
      this._centralApiBaseUri = this.getSubdomainUrl(environment.centralApiBaseUri, environment.subdomainPortal);
      this._collabAuthorityUrl = this.getSubdomainUrl(environment.collabAuthorityUrl, environment.subdomainPortal);
      this._collabApiUrl = this.getSubdomainUrl(environment.collabApiUrl, environment.subdomainPortal);
      this._collabWorkspaceUrl = this.getSubdomainUrl(environment.collabWorkspaceUrl, environment.subdomainPortal);
    }
  }

  /**
   * Updates the URLs for the Central API, Collab Server, and blob data (eg assets, content, publish).
   * @param centralInstance The Central instance to use for API, collab, and blob requests.
   */
  setCentralInstance(centralInstance?: CentralInstance) {
    this._centralApiBaseUri = this.getSubdomainUrl(centralInstance?.ApiUrl ?? environment.centralApiBaseUri, environment.subdomainPortal);
    this._collabAuthorityUrl = this.getSubdomainUrl(centralInstance?.CollabAuthorityUrl ?? environment.collabAuthorityUrl, environment.subdomainPortal);
    this._collabApiUrl = this.getSubdomainUrl(centralInstance?.CollabApiUrl ?? environment.collabApiUrl, environment.subdomainPortal);
    this._collabWorkspaceUrl = this.getSubdomainUrl(centralInstance?.CollabWorkspaceUrl ?? environment.collabWorkspaceUrl, environment.subdomainPortal);
    this._centralArchiveBaseUri = `${this.centralApiBaseUri}/${this.ArchivePath}`;
    this._centralAssetsBaseUri = `${this.centralApiBaseUri}/${this.AssetPath}`;
    this._centralContentBaseUri = `${this.centralApiBaseUri}/${this.ContentPath}`;
    this._centralPublishBaseUri = `${this.centralApiBaseUri}/${this.PublishPath}`;
    this._centralInstance = centralInstance;
    this.centralInstance$.next(centralInstance);
  }

  private getSubdomainUrl(baseUri: string, includeSubdomain: boolean): string {
    const baseUrl = new URL(baseUri);
    let subdomainPart = '';

    // If the portal should use a subdomain in its API requests
    if (includeSubdomain) {
      // Grab the portal's subdomain and combine it with the Central API base URI from the environment config
      const [subdomain] = this.window.location.hostname.split('.');
      subdomainPart = `${subdomain}.`;
    }

    return `${baseUrl.protocol}//${subdomainPart}${baseUrl.host}${baseUrl.pathname !== '/' ? baseUrl.pathname : ''}`;
  }
}
